import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';

import { cn } from '@/utils/cn';

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';

// Convert ISO date (YYYY-MM-DD) to US format (MM/DD/YYYY)
const formatDateToUS = (isoDate: string): string => {
    if (!isoDate) return '';
    const date = new Date(isoDate + 'T00:00:00'); // Add time to avoid timezone issues
    if (isNaN(date.getTime())) return isoDate; // Return original if invalid

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

// Convert US format (MM/DD/YYYY) to ISO date (YYYY-MM-DD)
const formatDateToISO = (usDate: string): string => {
    if (!usDate) return '';
    const match = usDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return usDate; // Return original if doesn't match MM/DD/YYYY

    const [, month, day, year] = match;
    const monthPadded = month.padStart(2, '0');
    const dayPadded = day.padStart(2, '0');
    return `${year}-${monthPadded}-${dayPadded}`;
};

const DateInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
    ({ className, value, onChange, ...props }, ref) => {
        const [displayValue, setDisplayValue] = React.useState<string>('');
        const hiddenInputRef = React.useRef<HTMLInputElement>(null);

        // Update display value when external value changes
        React.useEffect(() => {
            if (typeof value === 'string') {
                // If value looks like ISO date (YYYY-MM-DD), convert to US format
                if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    setDisplayValue(formatDateToUS(value));
                } else {
                    setDisplayValue(value);
                }
            }
        }, [value]);

        const handleVisibleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setDisplayValue(newValue);

            // Try to convert to ISO format and update hidden input
            const isoValue = formatDateToISO(newValue);
            if (hiddenInputRef.current) {
                hiddenInputRef.current.value = isoValue;
            }

            // Call the original onChange with the display value
            if (onChange) {
                const syntheticEvent = {
                    ...e,
                    target: { ...e.target, value: newValue },
                };
                onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
            }
        };

        const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const isoValue = e.target.value;
            const usValue = formatDateToUS(isoValue);
            setDisplayValue(usValue);

            // Call the original onChange with the US formatted value
            if (onChange) {
                const syntheticEvent = {
                    ...e,
                    target: { ...e.target, value: usValue },
                };
                onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
            }
        };

        return (
            <div className="relative flex items-center gap-2">
                {/* Hidden native date input for date picker functionality */}
                <input
                    ref={hiddenInputRef}
                    type="date"
                    className="pointer-events-none absolute inset-0 z-10 h-full w-full opacity-0"
                    value={formatDateToISO(displayValue)}
                    onChange={handleHiddenInputChange}
                    tabIndex={-1}
                />
                {/* Visible text input for manual editing */}
                <Input
                    {...props}
                    ref={ref}
                    className={cn('relative z-0', className)}
                    value={displayValue}
                    onChange={handleVisibleInputChange}
                    placeholder="MM/DD/YYYY"
                    readOnly={false}
                />
                {/* Calendar button to trigger native date picker */}
                <button
                    type="button"
                    className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded bg-transparent p-1 hover:bg-gray-100"
                    tabIndex={0}
                    aria-label="Open date picker"
                    onClick={() => {
                        if (hiddenInputRef.current) {
                            hiddenInputRef.current.focus();
                            hiddenInputRef.current.click();
                            if (hiddenInputRef.current.showPicker) {
                                try {
                                    hiddenInputRef.current.showPicker();
                                } catch (error) {
                                    console.error(error);
                                }
                            }
                        }
                    }}
                >
                    {/* Simple calendar icon (SVG) */}
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <rect x="3" y="5" width="18" height="16" rx="2" strokeWidth="2" />
                        <path d="M16 3v4M8 3v4M3 9h18" strokeWidth="2" />
                    </svg>
                </button>
            </div>
        );
    },
);
DateInput.displayName = 'DateInput';

interface FormDateInputProps {
    formMethods: UseFormReturn<any, any, any>;
    name: string;
    label?: string;
    placeholder?: string;
    description?: string;
    defaultValue?: string;
    required?: boolean;
}

const FormDateInput = (props: FormDateInputProps) => {
    return (
        <FormField
            defaultValue={props.defaultValue ?? ''}
            control={props.formMethods.control}
            name={props.name}
            render={({ field }) => (
                <FormItem className="flex flex-col gap-2">
                    {props.label && <FormLabel>{props.label}</FormLabel>}
                    <FormControl>
                        <DateInput {...field} placeholder={props.placeholder} required={props.required} />
                    </FormControl>
                    {props.description && <FormDescription>{props.description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
};

FormDateInput.displayName = 'FormDateInput';

export { DateInput, FormDateInput };
